import { reviewOfferCopy } from "./reviewOfferCopy";
import { ThanosReviewSubmittedDetail } from "./types";

// HUB-466: the disclosure/G2/consent copy is identical across programs today
// (no percent is mentioned in it) and this fixture has no way to know which
// program applies to the client viewing it — reviewOfferCopy.CHECKOUT_5's
// block is the canonical source for this program-agnostic legal copy until a
// program needs its own disclosure wording. Referenced via the full literal
// path (not an intermediate variable) so
// scripts/ftc-compliance-check.mjs's disclosure-presence check can still
// find it statically.

// Contract fixture (Obsidian oads-discount/contracts/thanos-widget-fixture.html,
// HUB-448/HUB-453). A real `<thanos-review-widget>` custom element with the
// SAME tag name, attributes, validation rules and DOM events as the widget
// D1 builds in phase 4 (HUB-455) — so mountThanosWidget.ts is the ONLY file
// that needs to change when the real widget ships (swap script-injection for
// this local registration).
//
// FTC 16 CFR Part 465: accepts submission at ANY rating 1-5, no sentiment
// steering, identical flow for every rating, incentivized-review disclosure
// + G2 notice shown before submit, min 25-char review text, consent by submitting.
const TAG_NAME = "thanos-review-widget";
const MIN_TEXT_LENGTH = 25;

const uuid = () =>
  "rev_xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

class ThanosReviewWidgetFixtureElement extends HTMLElement {
  private root!: ShadowRoot;

  private rating = 0;

  connectedCallback() {
    this.root = this.attachShadow({ mode: "open" });
    this.render();
  }

  private dispatchSubmitted(rating: number) {
    const detail: ThanosReviewSubmittedDetail = {
      review_id: uuid(),
      rating,
      submitted_at: new Date().toISOString(),
    };
    window.dispatchEvent(new CustomEvent("thanos:review-submitted", { detail }));
  }

  private dispatchError(message: string) {
    window.dispatchEvent(new CustomEvent("thanos:review-error", { detail: { code: "validation_error", message } }));
  }

  private render() {
    const orgId = this.getAttribute("org-id") ?? "";
    const sourceAttr = this.getAttribute("source") ?? "checkout_offer";
    // Contract §1 / reference fixture (thanos-widget-fixture.html:12): the
    // incentivized-review disclosure is shown "before submit" specifically
    // when this mount is incentivized — gated on the attribute the same way
    // the frozen reference fixture gates it, not rendered unconditionally.
    const incentivized = this.getAttribute("incentivized") === "true";

    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: Inter, system-ui, sans-serif; color: #1A2547; }
        .wrap { padding: 20px; max-width: 420px; }
        .stars { display: flex; gap: 6px; margin: 8px 0 16px; }
        .star { cursor: pointer; font-size: 24px; color: #CBD5E1; background: none; border: none; padding: 0; }
        .star[aria-pressed="true"], .star.filled { color: #F5A623; }
        textarea { width: 100%; min-height: 90px; border: 1px solid #E2E8F0; border-radius: 8px; padding: 8px; font: inherit; box-sizing: border-box; }
        .notice { font-size: 12px; color: #64748B; margin: 10px 0; }
        p.consent { font-size: 12.5px; color: #4B5563; line-height: 1.4; margin: 10px 0; }
        .count { font-size: 11px; color: #94A3B8; text-align: right; }
        button.submit { margin-top: 12px; background: #1A2547; color: #fff; border: none; border-radius: 8px; padding: 10px 16px; font-weight: 600; cursor: pointer; }
        button.submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .err { color: #B91C1C; font-size: 12px; min-height: 16px; }
      </style>
      <div class="wrap" data-cypress-id="thanosReviewWidgetFixture" data-org-id="${orgId}" data-source="${sourceAttr}">
        ${
          incentivized
            ? `<p class="notice" data-cypress-id="thanosReviewWidgetIncentiveDisclosure">${reviewOfferCopy.CHECKOUT_5.review.incentiveDisclosure}</p>`
            : ""
        }
        <p class="notice">${reviewOfferCopy.CHECKOUT_5.review.g2Notice}</p>
        <div class="stars" role="radiogroup" aria-label="Rating">
          ${[1, 2, 3, 4, 5]
            .map((n) => `<button type="button" class="star" data-star="${n}" aria-pressed="false">★</button>`)
            .join("")}
        </div>
        <textarea placeholder="Tell us about your experience (any rating welcome)" minlength="${MIN_TEXT_LENGTH}"></textarea>
        <div class="count">0 / ${MIN_TEXT_LENGTH} min characters</div>
        <p class="consent">${reviewOfferCopy.CHECKOUT_5.review.consent}</p>
        <div class="err" role="alert"></div>
        <button type="button" class="submit" disabled>Submit review</button>
      </div>
    `;

    const stars = Array.from(this.root.querySelectorAll<HTMLButtonElement>(".star"));
    const textarea = this.root.querySelector("textarea") as HTMLTextAreaElement;
    const count = this.root.querySelector(".count") as HTMLDivElement;
    const submitBtn = this.root.querySelector(".submit") as HTMLButtonElement;
    const err = this.root.querySelector(".err") as HTMLDivElement;

    const updateSubmitEnabled = () => {
      submitBtn.disabled = !(this.rating >= 1 && this.rating <= 5 && textarea.value.length >= MIN_TEXT_LENGTH);
    };

    stars.forEach((star) => {
      star.addEventListener("click", () => {
        this.rating = Number(star.dataset.star);
        stars.forEach((s) => {
          const filled = Number(s.dataset.star) <= this.rating;
          s.classList.toggle("filled", filled);
          s.setAttribute("aria-pressed", String(filled));
        });
        updateSubmitEnabled();
      });
    });

    textarea.addEventListener("input", () => {
      count.textContent = `${textarea.value.length} / ${MIN_TEXT_LENGTH} min characters`;
      updateSubmitEnabled();
    });

    submitBtn.addEventListener("click", () => {
      err.textContent = "";
      if (this.rating < 1 || this.rating > 5) {
        err.textContent = "Please select a rating.";
        return;
      }
      if (textarea.value.length < MIN_TEXT_LENGTH) {
        err.textContent = `Review must be at least ${MIN_TEXT_LENGTH} characters.`;
        return;
      }
      // Accepted at ANY rating, incl. 1 star - no gating, no sentiment steering.
      this.dispatchSubmitted(this.rating);
    });
  }
}

let registered = false;

/** Idempotent — safe to call from every mount (StrictMode double-invoke, remounts). */
export const ensureThanosWidgetFixtureRegistered = () => {
  if (registered || typeof window === "undefined" || !window.customElements) return;
  if (!window.customElements.get(TAG_NAME)) {
    window.customElements.define(TAG_NAME, ThanosReviewWidgetFixtureElement);
  }
  registered = true;
};
